<template>
  <div>
    <div class="flex flex-col gap-4 p-6 pb-8 2xl:p-9">
      <RfPromptHeader
        :id="path?.id"
        v-model:filtersData="filtersData"
        v-model:date="date"
        :actionList="experimentActionList"
        :status="currExperimentStatus"
        :segments="path?.segments"
        :hideAdditionalRow="!currExperiment?.is_active && !currExperiment?.is_completed"
        :appDevices="appsStore.app?.path_filter?.dimension_filters?.device_categories"
        :appLanguages="appsStore.app?.path_filter?.dimension_filters?.languages"
        :appDeviceTypes="
          appsStore.app?.flags?.device_type_analytics ? getCustomDeviceEntries(path) : []
        "
        :name="currExperiment?.name"
        :lockDatepickerPreset="allowDateEditing ? null : METRICS_PERIODS.since_start_date.value"
        lockDatepickerPresetMessage="To use a custom date range, apply a filter first"
        :startDate="currExperiment?.created_at"
        :possibleStatuses="
          currExperimentStatus.type === StatusesSimplifiedConstants.ended.type
            ? []
            : [
                currExperiment?.is_active
                  ? StatusesSimplifiedConstants.ended
                  : StatusesSimplifiedConstants.running,
              ]
        "
        :disabled="userStore.isDisabledWithMessage"
        :skeleton="skeleton"
        :description="`Experiment with ${path?.name}`"
        @back="goBack"
        @update:filtersData="fetchMetrics(true)"
        @update:date="v => ((datepickerDate = v), fetchMetrics())"
        @update:status="experimentModals.callExperimentDialog(currExperiment?.is_active)"
      />

      <RfBaseCard v-if="path && currExperiment && experimentTableMode" class="gap-3">
        <Transition mode="out-in">
          <RfBaseSkeleton v-if="skeleton" height="25" width="189" />
          <h2 v-else class="text-heading-1 text-beige-90">Stats</h2>
        </Transition>
        <RfExperimentUnifiedTable
          ref="experimentTable"
          v-model:pieActionGroupId="pieActionGroupId"
          :mode="experimentTableMode"
          :metrics="currExperimentMetrics"
          :experiment="currExperiment"
          :path="path"
          :metricsKey="datepickerDate?.preset?.value || 'since_start_date'"
          :timePeriod="[datepickerDate?.startDate, datepickerDate?.endDate]"
        />
      </RfBaseCard>

      <RfBaseCard
        v-if="
          skeleton ||
          (!currExperiment?.is_completed && currExperiment?.is_active) ||
          drilldownMetricsLength
        "
        class="flex flex-col gap-4"
      >
        <div class="flex items-center justify-between">
          <Transition mode="out-in">
            <RfBaseSkeleton v-if="skeleton" height="25" width="189" />
            <h2 v-else class="text-heading-1 text-beige-90">Performance</h2>
          </Transition>

          <Transition mode="out-in">
            <RfBaseSkeleton v-if="skeleton" height="40" width="185" />
            <RfButtonNew
              v-else-if="!currExperiment?.is_completed && currExperiment?.is_active"
              :disabled="userStore.isDisabledWithMessage"
              :tooltipMessage="
                currExperiment.stats_last_updated_text
                  ? `Last updated: ${currExperiment.stats_last_updated_text}`
                  : null
              "
              type="quaternary"
              @click="
                experimentsStore.getExperimentForceUpdate({
                  pathId: path.id,
                  expId: currExperiment.id,
                })
              "
            >
              <RfResetIcon class="h-6 w-6" /> Force update stats
            </RfButtonNew>
          </Transition>
        </div>
        <template
          v-if="
            (!currExperiment?.is_completed && currExperiment?.is_active) || drilldownMetricsLength
          "
        >
          <RfExperimentChart
            ref="experimentChart"
            :metrics="currExperimentMetrics"
            :metricsKey="datepickerDate?.preset?.value || 'since_start_date'"
            :path="path"
            :skeleton="skeleton"
          />
        </template>
      </RfBaseCard>
      <div v-if="isSurvey" class="flex">
        <RfSelect
          v-model="pieActionGroupId"
          class="mr-0 ml-auto"
          variant="outlined"
          :items="displayVariations"
          itemValue="name"
          itemKey="id"
          sameWidth
          @vue:mounted="pieActionGroupId = displayVariations?.[0]?.id || null"
        />
      </div>
      <RfExperimentChartAdditional
        v-if="
          skeleton ||
          (((!currExperiment?.is_completed && currExperiment?.is_active) ||
            drilldownMetricsLength ||
            (isSurvey && currExperiment?.is_completed)) &&
            path)
        "
        :metrics="currExperimentMetrics"
        :metricsKey="datepickerDate?.preset?.value || 'since_start_date'"
        :path="path"
        :selectedVariantId="experimentChart?.selectedVariantId ?? pieActionGroupId"
        :variants="experimentChart?.variants ?? []"
        :skeleton="skeleton"
        :isSurvey="isSurvey"
        :surveyMetrics="customMetrics"
        :surveyInputs="surveyInputs"
      />

      <RfExperimentDetails
        v-if="path && currExperiment"
        :skeleton="skeleton"
        :disableEdit="userStore.isDisabledWithMessage || isEditingDisabledWithMessage"
        :disableTraffic="userStore.isDisabledWithMessage || isEditingDisabledWithMessage"
        :disableSchedule="
          userStore.isDisabledWithMessage ||
          (currExperiment?.is_completed &&
            'This setting cannot be updated on a completed experiment')
        "
        :variations="currExperiment?.variations"
        :editable="currExperiment?.is_live_editable"
        :startDate="currExperiment?.start_date"
        :endDate="currExperiment?.end_date"
        :timezone="appsStore.app.timezone_offset"
        :createdAt="currExperiment?.created_at"
        :updatedAt="currExperiment?.updated_at"
        @editEditable="experimentModals.openUpdateEditableModal()"
        @editTraffic="experimentModals.openTrafficEditDialog()"
        @editSchedule="experimentModals.openScheduleModal()"
      />

      <RfExperimentVariations
        :disableAdd="
          userStore.isDisabledWithMessage ||
          (currExperiment?.is_active && 'New variants cannot be added to an active experiment') ||
          (currExperiment?.is_completed && 'New variants cannot be added to a completed experiment')
        "
        :skeleton="skeleton"
        :variants="displayVariations"
        :path="path"
        :currExperiment="currExperiment"
        :disabled="userStore.isDisabledWithMessage"
        @addVariant="addVariantDialog = true"
        @showEditVariant="showEditVariant"
        @showDeleteModal="showDeleteModal"
        @showVariantDetails="showVariantDetails"
      />
      <template v-if="path && currExperiment">
        <RfEditAbVariant
          :path="path"
          :editVariantDialog="addVariantDialog"
          :currExperiment="currExperiment"
          :isNew="true"
          title="Add variation"
          @closeEditVariantModal="closeAddVariantModal"
        />
        <RfEditAbVariant
          :key="variantDetail.id"
          :path="path"
          :variantDetail="variantDetail"
          :editVariantDialog="editVariantDialog"
          :currExperiment="currExperiment"
          :disabled="
            !currExperiment.is_live_editable &&
            !userStore.isCurrentSuperAdmin &&
            (currExperiment.is_active || currExperiment.is_completed)
          "
          title="Edit variation"
          @closeEditVariantModal="closeEditVariantModal"
        />
        <RfEditAbVariant
          :key="variantDetail.id + 'control'"
          :path="path"
          :editVariantDialog="showVariantDetailDialog"
          :currExperiment="currExperiment"
          :variantDetail="variantDetail"
          :disabled="true"
          title="Variation details"
          @closeEditVariantModal="closeVariantDetails"
        />
        <v-dialog
          v-model="cloneDialog"
          overlayOpacity="0.85"
          width="500"
          contentClass="show-variant-dialog"
          scrollable
        >
          <v-card flat>
            <v-card-title class="headline">Are you sure you want to clone?</v-card-title>
            <v-card-text>
              Cloning an experiment overwrites the configuration of the prompt. This cannot be
              undone.
            </v-card-text>
            <v-card-actions class="modal-card-actions-div">
              <v-btn
                size="large"
                class="cancel-btn"
                variant="outlined"
                width="100px"
                @click="cloneDialog = false"
                >Cancel</v-btn
              >
              <v-btn
                size="large"
                variant="flat"
                color="accent"
                style="width: 150px"
                @click="clone()"
                >Clone</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog
          v-model="deleteExpDialog"
          overlayOpacity="0.85"
          width="500"
          contentClass="show-variant-dialog"
          scrollable
        >
          <v-card flat>
            <v-card-title class="headline">Are you sure you want to delete?</v-card-title>
            <v-card-text>
              Deleting an experiment removes the experiment and all variations. This cannot be
              undone.
            </v-card-text>
            <v-card-actions class="modal-card-actions-div">
              <v-btn
                size="large"
                class="cancel-btn"
                variant="outlined"
                width="100px"
                @click="closeDeleteExpDialog"
                >Cancel</v-btn
              >
              <v-btn
                size="large"
                variant="flat"
                color="error"
                style="width: 150px"
                @click="submitDeleteExperiment"
                >Delete</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog
          v-model="showDeleteDialog"
          overlayOpacity="0.85"
          width="500"
          contentClass="show-variant-dialog"
          scrollable
        >
          <v-card flat>
            <v-card-title class="headline">Are you sure you want to delete?</v-card-title>
            <v-card-text>
              Deleting a variation removes all information about this variation. This cannot be
              undone.
            </v-card-text>
            <v-card-actions class="modal-card-actions-div">
              <v-btn
                size="large"
                class="cancel-btn"
                variant="outlined"
                width="100px"
                @click="closeDeleteModal"
                >Cancel</v-btn
              >
              <v-btn
                size="large"
                variant="flat"
                color="error"
                style="width: 150px"
                @click="deleteVariant(variantDetail)"
                >Delete</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog
          v-model="editNameDialog"
          overlayOpacity="0.85"
          width="500"
          contentClass="show-variant-dialog"
        >
          <v-card flat>
            <v-card-title class="headline">Experiment Name</v-card-title>
            <v-card-text>
              <v-text-field
                v-model.trim="experimentName"
                density="compact"
                variant="outlined"
                autofocus
                persistentPlaceholder
                placeholder="Name"
                label="Name"
                hideDetails
                style="margin: 10px 0"
              />
            </v-card-text>
            <v-card-actions class="modal-card-actions-div">
              <v-btn
                size="large"
                class="cancel-btn"
                variant="outlined"
                width="100px"
                @click="closeEditNameDialog"
                >Cancel</v-btn
              >
              <v-btn
                size="large"
                variant="flat"
                color="accent"
                style="width: 150px"
                @click="submitNameUpdate"
                >Save</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
        <RfExperimentModals
          ref="experimentModals"
          :selectedExperiment="currExperiment"
          :currExperimentStatus="currExperimentStatus"
          :showPromptMessage="!path?.is_enabled"
          @onStart="onStart"
          @updateEditable="saveExperiment({ is_live_editable: !currExperiment.is_live_editable })"
          @updateTraffic="
            ({ variations, runPrompt }) => saveExperiment({ variations }, { runPrompt })
          "
          @updateSchedule="schedule => saveExperiment({ ...schedule })"
        />
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { cloneDeep } from "lodash-es";

import RfEditAbVariant from "@/components/RfPathCreate/RfRetentionExperiment/RfEditAbVariant.vue";
import RfExperimentUnifiedTable from "@/blocks/RfExperiments/RfExperimentUnifiedTable.vue";
import RfSelect from "@/components/inputs/RfSelect.vue";
import RfBaseCard from "@/components/cards/RfBaseCard.vue";
import RfDownloadIcon from "@/components/icons/RfDownloadIcon.vue";
import RfPromptHeader from "@/blocks/RfPrompt/RfPromptHeader.vue";

import { useToastsStore } from "@/pinia/toastsStore";
import { useLegacyLastErrorStore } from "@/pinia/legacyLastErrorStore";
import { useExperimentsStore } from "@/pinia/experimentsStore";
import { usePromptStore } from "@/pinia/promptStore";
import { useUserStore } from "@/pinia/userStore";
import { useAppsStore } from "@/pinia/appsStore";

import { getCustomDeviceEntries } from "@/utils/getDeviceName";
import { getExperimentLink, getGuidePromptLink, getPromptLink } from "@/utils/getLink";
import {
  isSurvey as isSurveyHelper,
  isInvisible,
  isLocalized as isLocalizedHelper,
} from "@/utils/prompts/promptHelpers";
import {
  PATH_CUSTOM_METRIC_PIECHART,
  PATH_CUSTOM_METRIC_SURVEY,
} from "@/utils/constants/CustomMetricsConstants";
import { getSurveyInputs } from "@/utils/metricsHelpers";
import { useDynamicTitle } from "@/utils/composables/useDynamicTitle";
import { useRecentActivity } from "@/utils/composables/useRecentActivity";
import { useFilterData } from "@/utils/composables/useFilterData";
import RfExperimentDetails from "@/blocks/RfExperiments/RfExperimentDetails.vue";
import RfExperimentChart from "@/blocks/RfExperiments/RfExperimentChart.vue";
import RfExperimentChartAdditional from "@/blocks/RfExperiments/RfExperimentChartAdditional.vue";
import { StatusesSimplifiedConstants } from "@/utils/constants/PromptStatusesConstants";
import { METRICS_PERIODS } from "@/utils/constants/MetricsConstants";
import RfRenameIcon from "@/components/icons/RfRenameIcon.vue";
import RfExperimentModals from "@/blocks/RfExperiments/RfExperimentModals.vue";
import RfDeleteIcon from "@/components/icons/RfDeleteIcon.vue";
import RfCloneIcon from "@/components/icons/RfCloneIcon.vue";
import RfBaseSkeleton from "@/components/skeletons/RfBaseSkeleton.vue";
import RfResetIcon from "@/components/icons/RfResetIcon.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfExperimentVariations from "@/blocks/RfExperiments/RfExperimentVariations.vue";

const experimentChart = ref(null);

const sequenceId = ref(null);

const route = useRoute();
const router = useRouter();

// Stores
const toastsStore = useToastsStore();
const experimentsStore = useExperimentsStore();
const promptStore = usePromptStore();
const userStore = useUserStore();
const legacyLastErrorStore = useLegacyLastErrorStore();
const recentActivity = useRecentActivity();
const appsStore = useAppsStore();

// Composables
const { filtersData, filterDataKeys, getDimensionFilters } = useFilterData();
const drilldownMetricsLength = computed(
  () => Object.values(filterDataKeys.value || {}).flat()?.length,
);

// Template refs
const experimentTable = useTemplateRef("experimentTable");
const experimentModals = useTemplateRef("experimentModals");

// State
const datepickerDate = ref({});
const date = ref(null);
const pieActionGroupId = ref(null);
watch(
  () => experimentChart.value?.selectedVariantId,
  id => (pieActionGroupId.value = id ?? null),
);
const addVariantDialog = ref(false);
const editVariantDialog = ref(false);
const showDeleteDialog = ref(false);
const showVariantDetailDialog = ref(false);
const variantDetail = ref({});
const cloneDialog = ref(false);
const deleteExpDialog = ref(false);
const editNameDialog = ref(false);
const experimentName = ref(null);
const isUpdating = ref(false);

// Computed — store accessors
const lastError = computed(() => legacyLastErrorStore.lastError);
const path = computed(() => promptStore.prompt);
const currExperiment = computed(() => experimentsStore.currExperiment);
const currExperimentMetrics = computed(() => experimentsStore.currExperimentMetrics);
const currExperimentCustomMetrics = computed(() => experimentsStore.currExperimentCustomMetrics);
const experimentActionList = computed(() => [
  {
    icon: RfRenameIcon,
    title: "Rename",
    onClick: () => openEditNameDialog(),
  },
  {
    if: !(
      userStore.isDisabledWithMessage ||
      !currExperiment.value?.is_completed ||
      !!path.value?.experiment ||
      isLocalized.value
    ),
    icon: RfCloneIcon,
    title: "Clone",
    onClick: () => (cloneDialog.value = true),
  },
  {
    if: !(!currExperiment.value?.is_active && !currExperiment.value?.is_completed),
    icon: RfDownloadIcon,
    title: "Export to CSV",
    onClick: () => exportExperimentsToCSV(),
  },
  {
    if: !(
      userStore.isDisabledWithMessage ||
      currExperiment.value?.is_active ||
      currExperiment.value?.is_completed
    ),
    icon: RfDeleteIcon,
    title: "Delete",
    onClick: () => openDeleteExpDialog(),
  },
]);
const allowDateEditing = computed(
  () =>
    (currExperiment.value?.is_active && !currExperiment.value?.is_completed) ||
    drilldownMetricsLength.value,
);
const currExperimentStatus = computed(() => {
  if (currExperiment.value?.is_active && !currExperiment.value?.is_completed)
    return StatusesSimplifiedConstants.running;

  if (!currExperiment.value?.is_active && !currExperiment.value?.is_completed)
    return StatusesSimplifiedConstants.ready;

  return StatusesSimplifiedConstants.ended;
});

const isEditingDisabledWithMessage = computed(() => {
  if (currExperiment.value?.is_completed)
    return "This setting cannot be updated on a completed experiment";
  if (currExperiment.value?.is_active)
    return "This setting cannot be updated on an active experiment";
  return false;
});
const isLocalized = computed(
  () => isLocalizedHelper(path.value) && "Disabled for localized prompts",
);
const displayVariations = computed(() =>
  isInvisible(path.value)
    ? currExperiment.value?.variations
    : currExperiment.value?.variations.filter(item => item.is_visible && !item.is_control),
);
const isSurvey = computed(() => isSurveyHelper(path.value));
const experimentTableMode = computed(() => {
  if (!currExperiment.value) return null;
  if (
    (!currExperiment.value.is_completed && currExperiment.value.is_active) ||
    drilldownMetricsLength.value
  )
    return "active";
  if (!currExperiment.value.is_active && currExperiment.value.is_completed) return "completed";
  return null;
});
const customMetrics = computed(
  () =>
    currExperimentCustomMetrics.value?.[
      isSurvey.value ? PATH_CUSTOM_METRIC_SURVEY : PATH_CUSTOM_METRIC_PIECHART
    ]?.[pieActionGroupId.value]?.[datepickerDate.value?.preset?.value]?.agg_data?.[2] || {},
);
const surveyInputs = computed(() =>
  getSurveyInputs(displayVariations.value?.find(({ id }) => id === pieActionGroupId.value)),
);

// Watchers
watch(currExperiment, () => {
  if (isUpdating.value) {
    toastsStore.create({ type: "success", body: "Experiment updated!" });
    closeAddVariantModal();
    isUpdating.value = false;
  }
});

watch(lastError, to => {
  if (to) toastsStore.create({ type: "error", body: to.message });
});

// Lifecycle

const skeleton = computed(() => !(path.value?.id && currExperiment.value?.id));

onMounted(async () => {
  sequenceId.value = route.params?.sid || null;
  useDynamicTitle(() => {
    const exp = currExperiment.value?.name;
    const prompt = path.value?.name;
    return exp && prompt && `${exp} | ${prompt}`;
  });

  const appId = route.params.aid;
  const pathId = route.params.pid;
  await Promise.all([
    promptStore.getPrompt({ appId, pathId }),
    experimentsStore.getExperiment({ pathId, expId: route.params.eid }),
  ]);
  recentActivity.updatePathActivity(appId, pathId);
  if (
    !(!currExperiment.value?.is_completed && currExperiment.value?.is_active) &&
    isSurvey.value &&
    currExperiment.value?.is_completed
  ) {
    pieActionGroupId.value = displayVariations.value?.[0]?.id || null;
    await fetchMetrics({ metric_periods: [{ period: "since_start_date" }] });
  }
});

const goBack = () => {
  if (sequenceId.value)
    return router.push(getGuidePromptLink(path.value.id, sequenceId.value, appsStore.app?.id));
  return router.push(getPromptLink(path.value.id, appsStore.app?.id));
};

function exportExperimentsToCSV() {
  experimentTable.value?.exportToCSV(filterDataKeys.value);
}

async function clone() {
  const clonedExperiment = await experimentsStore.cloneExperiment({
    pathId: path.value.id,
    experimentId: currExperiment.value.id,
  });
  await experimentsStore.getExperiment({ pathId: path.value.id, expId: clonedExperiment.id });
  router.push({ path: getExperimentLink(clonedExperiment.id, path.value.id, route.params.aid) });
  cloneDialog.value = false;
}

const saveExperiment = async (exp = {}, { runPrompt = false } = { runPrompt: false }) => {
  await experimentsStore.updateExperiment({
    pathId: path.value.id,
    expId: currExperiment.value.id,
    modelExperiment: { ...currExperiment.value, ...(exp || {}) },
  });
  if (runPrompt) onStart(false);
};

async function onStart(pausePromo) {
  isUpdating.value = true;
  const appId = route.params.aid;
  const pathId = path.value.id;
  await experimentsStore.runExperiment({
    appId,
    pathId,
    expId: currExperiment.value.id,
    isStart: !currExperiment.value?.is_active,
  });
  if (currExperiment.value?.is_active)
    promptStore.mutateUpdatePrompt({ prompt: { ...path.value, experiment: null } });
  if (pausePromo) {
    const modelPath = cloneDeep(path.value);
    modelPath.is_enabled = false;
    promptStore
      .updatePrompt({ appId, pathId, modelPath })
      .then(() => null)
      .catch(() => null);
  }
}

function closeAddVariantModal() {
  addVariantDialog.value = false;
}

function closeEditVariantModal() {
  editVariantDialog.value = false;
}

function showEditVariant(variant) {
  editVariantDialog.value = true;
  variantDetail.value = variant;
}

function showDeleteModal(variant) {
  showDeleteDialog.value = true;
  variantDetail.value = variant;
}

function closeDeleteModal() {
  showDeleteDialog.value = false;
}

function showVariantDetails(variant) {
  showVariantDetailDialog.value = true;
  variantDetail.value = variant;
}

function closeVariantDetails() {
  showVariantDetailDialog.value = false;
}

function deleteVariant(variant) {
  const modelExperiment = {
    ...currExperiment.value,
    variations: currExperiment.value.variations.filter(({ id }) => id !== variant.id),
  };
  experimentsStore.updateExperiment({
    pathId: path.value.id,
    expId: currExperiment.value.id,
    modelExperiment,
  });
  toastsStore.create({ type: "success", body: `Variation ${variant.name} deleted` });
  showDeleteDialog.value = false;
}

function openDeleteExpDialog() {
  deleteExpDialog.value = true;
}

function closeDeleteExpDialog() {
  deleteExpDialog.value = false;
}

function submitDeleteExperiment() {
  closeDeleteExpDialog();
  const experimentId = currExperiment.value.id;
  const pathId = path.value.id;
  experimentsStore.deleteExperiment({ pathId, experimentId }).then(() => {
    router.replace({ path: `/apps/${route.params.aid}/retentions/${pathId}` });
  });
}

function openEditNameDialog() {
  experimentName.value = currExperiment.value.name;
  editNameDialog.value = true;
}

function closeEditNameDialog() {
  experimentName.value = currExperiment.value.name;
  editNameDialog.value = false;
}

function submitNameUpdate() {
  currExperiment.value.name = experimentName.value;
  saveExperiment();
  closeEditNameDialog();
}

async function fetchMetrics(force = false) {
  const dimensionFilters = getDimensionFilters();
  const isActive = !currExperiment.value?.is_completed && currExperiment.value?.is_active;
  if (!isActive && !dimensionFilters?.dimension_filters && !currExperiment.value?.is_completed)
    return;
  const period = datepickerDate.value?.preset?.value || "since_start_date";
  if (Object.entries(currExperimentMetrics.value).find(([_, v]) => v[period]) && !force) return;
  const params = {
    metric_periods: [
      {
        period,
        ...(period === "custom" && {
          date_range: [
            datepickerDate.value?.startDate.format("YYYY-MM-DD"),
            datepickerDate.value?.endDate.format("YYYY-MM-DD"),
          ],
        }),
      },
    ],
    ...dimensionFilters,
    experiment_id: route.params.eid,
  };
  await experimentsStore.getExperimentMetrics({
    pathId: route.params.pid,
    appId: route.params.aid,
    params,
  });
  if (isSurvey.value) {
    const variantIds = displayVariations.value?.map(({ id }) => id) || [];
    if (!variantIds.includes(pieActionGroupId.value))
      pieActionGroupId.value = variantIds[0] || null;
    experimentsStore.getExperimentCustomMetrics({
      pathId: route.params.pid,
      id: route.params.eid,
      params: { ...params, metric_keys: [PATH_CUSTOM_METRIC_SURVEY] },
    });
  }
}
</script>
